import { Button } from '@/components/ui/Button'
import { Select } from '@/components/ui/Select'
import { formatNumber } from '@/lib/format'
import type { PageMeta } from '@/types/api'

export interface PaginationProps {
  meta: PageMeta
  onPageChange: (page: number) => void
  onPageSizeChange: (size: number) => void
}

const PAGE_SIZES = [25, 50, 100, 200]

/** Window of page numbers around the current page, with ellipsis markers. */
function pageWindow(current: number, total: number): Array<number | 'gap'> {
  if (total <= 7) return Array.from({ length: total }, (_, index) => index + 1)

  const pages = new Set<number>([1, total, current])
  for (let offset = 1; offset <= 1; offset += 1) {
    if (current - offset > 1) pages.add(current - offset)
    if (current + offset < total) pages.add(current + offset)
  }

  const sorted = [...pages].sort((a, b) => a - b)
  const output: Array<number | 'gap'> = []
  sorted.forEach((page, index) => {
    if (index > 0 && page - sorted[index - 1] > 1) output.push('gap')
    output.push(page)
  })
  return output
}

const Chevron = ({ back }: { back?: boolean }) => (
  <svg
    viewBox="0 0 12 12"
    className={back ? 'h-3 w-3 rotate-180' : 'h-3 w-3'}
    aria-hidden="true"
  >
    <path
      d="M4.5 2.5L8 6l-3.5 3.5"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

export function Pagination({ meta, onPageChange, onPageSizeChange }: PaginationProps) {
  const { page, pageSize, totalItems, totalPages } = meta
  const first = totalItems === 0 ? 0 : (page - 1) * pageSize + 1
  const last = Math.min(page * pageSize, totalItems)

  return (
    <nav
      aria-label="Results pages"
      className="flex flex-col-reverse items-center justify-between gap-3 border-t border-line px-4 py-3 sm:flex-row"
    >
      <div className="flex items-center gap-3">
        <p className="tabular text-xs whitespace-nowrap text-ink-muted">
          {totalItems === 0
            ? 'No results'
            : `${formatNumber(first)}–${formatNumber(last)} of ${formatNumber(totalItems)}`}
        </p>
        <Select
          aria-label="Rows per page"
          selectSize="sm"
          value={String(pageSize)}
          options={PAGE_SIZES.map((size) => ({
            value: String(size),
            label: `${size} / page`,
          }))}
          onChange={(event) => onPageSizeChange(Number(event.target.value))}
          className="w-26"
        />
      </div>

      {totalPages > 1 ? (
        <div className="flex items-center gap-1">
          <Button
            size="sm"
            variant="secondary"
            onClick={() => onPageChange(page - 1)}
            disabled={page <= 1}
            aria-label="Previous page"
            icon={<Chevron back />}
          >
            <span className="hidden sm:inline">Prev</span>
          </Button>

          <ul className="hidden items-center gap-1 sm:flex">
            {pageWindow(page, totalPages).map((entry, index) =>
              entry === 'gap' ? (
                <li
                  key={`gap-${index}`}
                  className="px-1 text-xs text-ink-faint"
                  aria-hidden="true"
                >
                  …
                </li>
              ) : (
                <li key={entry}>
                  <Button
                    size="sm"
                    variant={entry === page ? 'primary' : 'ghost'}
                    onClick={() => onPageChange(entry)}
                    aria-current={entry === page ? 'page' : undefined}
                    aria-label={`Page ${entry}`}
                    className="tabular min-w-8 px-2"
                  >
                    {entry}
                  </Button>
                </li>
              ),
            )}
          </ul>

          <span className="tabular px-2 text-xs text-ink-secondary sm:hidden">
            {page} / {totalPages}
          </span>

          <Button
            size="sm"
            variant="secondary"
            onClick={() => onPageChange(page + 1)}
            disabled={page >= totalPages}
            aria-label="Next page"
          >
            <span className="hidden sm:inline">Next</span>
            <Chevron />
          </Button>
        </div>
      ) : null}
    </nav>
  )
}
